import React, { useState } from "react";
import axios from "axios";
import {
    Paper,
    Group,
    Text,
    Button,
    Table,
    Badge,
    Modal,
    Stack,
    Select,
    NumberInput,
    Switch,
    ActionIcon,
    Tooltip,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconPlus, IconPencil, IconTrash } from "@tabler/icons-react";
import type {
    TransportRouteLocationDto,
    TransportRouteStopDto,
} from "../types";

const apiUrl = process.env.REACT_APP_API_URL;
const RED = "#ff5e5e";

type Props = {
    selectedRouteId: number | null;
    routeStops: TransportRouteStopDto[];
    locations: TransportRouteLocationDto[];
    refreshRouteStops: () => Promise<void>;
};

export default function TransportRouteStopsTab({
    selectedRouteId,
    routeStops,
    locations,
    refreshRouteStops,
}: Props) {
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<TransportRouteStopDto | null>(null);

    const [transportRouteLocationID, settransportRouteLocationID] = useState<string | null>(null);
    const [stopOrder, setStopOrder] = useState<number>(1);
    const [isActive, setIsActive] = useState(true);
    const [saving, setSaving] = useState(false);
    const [offsetMinutes, setOffsetMinutes] = useState<number>(0);

    const locationOptions = locations
        .filter((l) => l.isActive)
        .map((l) => ({
            value: String(l.transportRouteLocationID),
            label: l.nameDK,
        }));

    const openCreate = () => {
        setEditing(null);
        settransportRouteLocationID(null);
        setStopOrder(routeStops.length + 1);
        setOffsetMinutes(0);
        setIsActive(true);
        setModalOpen(true);
    };

    const openEdit = (stop: TransportRouteStopDto) => {
        setEditing(stop);
        settransportRouteLocationID(String(stop.transportRouteLocationID));
        setOffsetMinutes(stop.offsetMinutes ?? 0);
        setStopOrder(stop.stopOrder);
        setIsActive(stop.isActive);

        setModalOpen(true);
    };

    const save = async () => {
        if (!selectedRouteId) return;

        if (!transportRouteLocationID) {
            showNotification({
                title: "Mangler",
                message: "Vælg location",
                color: "red",
            });
            return;
        }

        try {
            setSaving(true);

            if (!editing) {
                await axios.post(`${apiUrl}/api/transport-route/admin/route-stops`, {
                    transportRouteID: selectedRouteId,
                    transportRouteLocationID: Number(transportRouteLocationID),
                    stopOrder,
                    offsetMinutes,
                });

                showNotification({
                    title: "Gemt",
                    message: "Stop oprettet",
                });
            } else {
                await axios.put(
                    `${apiUrl}/api/transport-route/admin/route-stops/${editing.transportRouteStopID}`,
                    {
                        transportRouteLocationID: Number(transportRouteLocationID),
                        stopOrder,
                        offsetMinutes,
                        isActive,
                    }
                );

                showNotification({
                    title: "Gemt",
                    message: "Stop opdateret",
                });
            }

            setModalOpen(false);
            await refreshRouteStops();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke gemme route stop",
                color: "red",
            });
        } finally {
            setSaving(false);
        }
    };

    const disableStop = async (id: number) => {
        try {
            await axios.delete(`${apiUrl}/api/transport-route/admin/route-stops/${id}`);
            showNotification({
                title: "OK",
                message: "Stop deaktiveret",
            });
            await refreshRouteStops();
        } catch (err) {
            console.error(err);
            showNotification({
                title: "Fejl",
                message: "Kunne ikke deaktivere stop",
                color: "red",
            });
        }
    };

    return (
        <>
            <Group justify="space-between" mb="sm">
                <Text fw={700}>Route stops</Text>

                <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={openCreate}
                    disabled={!selectedRouteId}
                    style={{ backgroundColor: RED }}
                >
                    Opret stop
                </Button>
            </Group>

            {!selectedRouteId ? (
                <Paper withBorder p="md" radius="md">
                    <Text c="dimmed">Vælg først en rute ovenfor.</Text>
                </Paper>
            ) : (
                <Paper withBorder p="md" radius="md">
                    <Table.ScrollContainer minWidth={800}>
                        <Table striped highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Location</Table.Th>
                                    <Table.Th>Rækkefølge</Table.Th>
                                    <Table.Th>Offset (min)</Table.Th>
                                    <Table.Th>Status</Table.Th>
                                    <Table.Th></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {routeStops.map((stop) => (
                                    <Table.Tr key={stop.transportRouteStopID}>
                                        <Table.Td>{stop.locationName}</Table.Td>
                                        <Table.Td>{stop.stopOrder}</Table.Td>
                                        <Table.Td>{stop.offsetMinutes}</Table.Td>
                                        <Table.Td>
                                            <Badge
                                                color={stop.isActive ? "green" : "gray"}
                                                variant="light"
                                            >
                                                {stop.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Group justify="flex-end" gap="xs">
                                                <Tooltip label="Rediger">
                                                    <ActionIcon
                                                        variant="light"
                                                        onClick={() => openEdit(stop)}
                                                    >
                                                        <IconPencil size={16} />
                                                    </ActionIcon>
                                                </Tooltip>

                                                <Tooltip label="Deaktiver">
                                                    <ActionIcon
                                                        color="red"
                                                        variant="light"
                                                        onClick={() =>
                                                            disableStop(stop.transportRouteStopID)
                                                        }
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                </Paper>
            )}

            <Modal
                opened={modalOpen}
                onClose={() => setModalOpen(false)}
                title={editing ? "Rediger stop" : "Opret stop"}
                centered
            >
                <Stack>
                    <Select
                        label="Location"
                        data={locationOptions}
                        value={transportRouteLocationID}
                        onChange={settransportRouteLocationID}
                        searchable
                    />
                    <NumberInput
                        label="Offset minutter"
                        value={offsetMinutes}
                        onChange={(v) => setOffsetMinutes(Number(v) || 0)}
                        min={0}
                    />

                    <NumberInput
                        label="Rækkefølge"
                        value={stopOrder}
                        onChange={(v) => setStopOrder(Number(v) || 1)}
                        min={1}
                    />

                    {editing && (
                        <Switch
                            label="Aktiv"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.currentTarget.checked)}
                        />
                    )}

                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setModalOpen(false)}>
                            Annullér
                        </Button>
                        <Button onClick={save} loading={saving}>
                            Gem
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}